import React, { useState } from 'react';
import { Modal, ModalHeader, ModalContent, ModalFooter } from '@/shared/components/ui/Modal';
import { Icon } from '@/shared/icons';
import type { ApplyCandidate } from './types';

interface ApplyToPagesModalProps {
  candidates: ApplyCandidate[];
  onCancel: () => void;
  /** Salva sem replicar para outras páginas. */
  onOnlyThisPage: () => void;
  /** Salva replicando os campos alterados: candidateKey -> pageIds selecionados. */
  onApply: (selection: Record<string, string[]>) => void;
}

/**
 * Modal exibida no Salvar quando blocos alterados existem em outras páginas.
 * Copia apenas os CAMPOS ALTERADOS (não o config inteiro) para as páginas marcadas.
 */
export const ApplyToPagesModal: React.FC<ApplyToPagesModalProps> = ({
  candidates, onCancel, onOnlyThisPage, onApply,
}) => {
  const [selection, setSelection] = useState<Record<string, string[]>>(() =>
    Object.fromEntries(candidates.map((c) => [c.key, c.targets.map((t) => t.pageId)]))
  );

  const toggle = (key: string, pageId: string) => {
    setSelection((prev) => {
      const current = prev[key] || [];
      return {
        ...prev,
        [key]: current.includes(pageId) ? current.filter((p) => p !== pageId) : [...current, pageId],
      };
    });
  };

  const totalSelected = new Set(Object.values(selection).flat()).size;

  return (
    <Modal isOpen onClose={onCancel} maxWidth="lg">
      <ModalHeader onClose={onCancel}>
        {/* F74: título de modal = 18px (text-lg font-semibold) */}
        <h2 className="text-lg font-semibold">Aplicar nas outras páginas?</h2>
      </ModalHeader>
      <ModalContent>
        <div className="space-y-5" data-testid="apply-to-pages-modal">
          {candidates.map((cand) => (
            <div key={cand.key} className="space-y-2">
              <p className="text-sm text-gray-700">
                Você alterou <strong>{cand.fieldLabels.join(', ')}</strong> no bloco{' '}
                <strong>{cand.blockName}</strong> da página <strong>{cand.sourcePageName}</strong>.
                Este bloco também existe em:
              </p>
              <div className="space-y-1">
                {cand.targets.map((target) => {
                  const checked = (selection[cand.key] || []).includes(target.pageId);
                  return (
                    <button
                      key={target.pageId}
                      type="button"
                      onClick={() => toggle(cand.key, target.pageId)}
                      className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg border text-left text-sm"
                      style={{
                        borderColor: checked ? '#8b5cf6' : '#e5e7eb',
                        backgroundColor: checked ? 'rgba(139,92,246,0.06)' : '#ffffff',
                        color: '#334155',
                      }}
                      data-testid={`apply-target-${cand.key}-${target.pageId}`}
                    >
                      <Icon
                        name={checked ? 'check-square' : 'square'}
                        className="w-4 h-4 flex-shrink-0"
                        style={{ color: checked ? '#8b5cf6' : '#94a3b8' }}
                      />
                      {target.pageName}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </ModalContent>
      <ModalFooter>
        <button
          type="button"
          onClick={onOnlyThisPage}
          className="px-3 py-2 rounded-lg text-sm font-medium border"
          style={{ borderColor: '#e5e7eb', color: '#334155' }}
          data-testid="apply-only-this-page-button"
        >
          Somente nesta página
        </button>
        <button
          type="button"
          onClick={() => onApply(selection)}
          disabled={totalSelected === 0}
          className="px-3 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
          style={{ backgroundColor: '#8b5cf6', color: '#ffffff' }}
          data-testid="apply-confirm-button"
        >
          Aplicar em {totalSelected} {totalSelected === 1 ? 'página' : 'páginas'}
        </button>
      </ModalFooter>
    </Modal>
  );
};
